import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';

const PUBLISHER_JWT_KEY = 'publisher_jwt';
const API_URL = process.env.REACT_APP_API_URL || 'https://videoreel-api-habwcma9cygwhphm.westus-01.azurewebsites.net';
const getToken = () => localStorage.getItem(PUBLISHER_JWT_KEY) || '';

const EMAIL_TYPES = [
  { value: 'all', label: 'All emails' },
  { value: 'welcome', label: 'Welcome' },
  { value: 'balance_cleared', label: 'Balance-cleared' },
  { value: 'points_reminder', label: 'Points reminder' },
  { value: 'communications', label: 'Communications' },
  { value: 'test', label: 'Test sends (to me)' },
];

// Tiles: key must match the summary counts + the drill-down status param.
const TILES = [
  { key: 'delivered', label: 'Delivered', color: '#2ecc71' },
  { key: 'opened', label: 'Opened', color: '#65c2d5', soft: true },
  { key: 'bounced', label: 'Bounced', color: '#ff6b7a' },
  { key: 'complained', label: 'Spam complaints', color: '#f0a020' },
  { key: 'delivery_delayed', label: 'Delayed', color: '#c9a227' },
  { key: 'pending', label: 'Pending', color: 'rgba(255,255,255,0.5)' },
];

// ─── Styled ───────────────────────────────────────────────────────────────────
const Wrap = styled.div`color: white;`;
const Title = styled.h1`
  font-size: 1.35rem; font-weight: 700; margin: 0 0 4px;
  background: linear-gradient(90deg, #fff, rgba(255,255,255,0.7));
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
`;
const SubTitle = styled.p`font-size: 0.82rem; color: rgba(255,255,255,0.4); margin: 0 0 20px;`;
const Row = styled.div`display: flex; gap: 12px; align-items: center; flex-wrap: wrap; margin-bottom: 18px;`;
const Select = styled.select`
  background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.12); border-radius: 8px;
  color: white; font-size: 0.88rem; padding: 7px 10px; outline: none; cursor: pointer;
  &:focus { border-color: rgba(255,71,87,0.5); } option { background: #1a1a2e; }
`;
const TileGrid = styled.div`display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 14px; margin-bottom: 8px;`;
const Tile = styled.button`
  text-align: left; background: rgba(255,255,255,0.04);
  border: 1px solid ${p => p.$active ? p.$color : 'rgba(255,255,255,0.09)'};
  border-radius: 14px; padding: 18px; cursor: pointer; transition: border-color 0.15s;
  &:hover { border-color: ${p => p.$color}; }
`;
const TileNum = styled.div`font-size: 1.9rem; font-weight: 700; line-height: 1; color: ${p => p.$color};`;
const TileLabel = styled.div`font-size: 0.8rem; color: rgba(255,255,255,0.6); margin-top: 8px; display: flex; align-items: center; gap: 6px;`;
const SoftTag = styled.span`font-size: 0.62rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #f0a020; border: 1px solid rgba(240,160,32,0.4); border-radius: 5px; padding: 1px 5px;`;
const Note = styled.p`font-size: 0.75rem; color: rgba(255,255,255,0.35); margin: 4px 0 20px;`;
const Section = styled.div`background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); border-radius: 16px; padding: 20px 22px; margin-top: 8px;`;
const SectionTitle = styled.div`font-size: 0.82rem; font-weight: 600; color: rgba(255,255,255,0.55); margin-bottom: 14px;`;
const Table = styled.table`width: 100%; border-collapse: collapse; font-size: 0.85rem;`;
const Th = styled.th`text-align: left; padding: 8px 10px; font-size: 0.72rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: rgba(255,255,255,0.35); border-bottom: 1px solid rgba(255,255,255,0.06);`;
const Td = styled.td`padding: 9px 10px; color: rgba(255,255,255,0.78); border-bottom: 1px solid rgba(255,255,255,0.04);`;
const Empty = styled.div`text-align: center; padding: 34px 0; color: rgba(255,255,255,0.3); font-size: 0.88rem;`;
const ErrMsg = styled.p`color: #ff6b7a; font-size: 0.82rem; margin: 10px 0 0;`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? '—' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
};
const typeLabel = (t) => (EMAIL_TYPES.find(e => e.value === t) || {}).label || t;

export default function PublisherDeliverability() {
  const [pubs, setPubs] = useState([]);
  const [pubId, setPubId] = useState('');
  const [month, setMonth] = useState('');
  const [emailType, setEmailType] = useState('all');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [drillStatus, setDrillStatus] = useState(null);
  const [drill, setDrill] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

  // Month options: current + previous 3 (UTC, to match server stamps).
  const monthOptions = (() => {
    const opts = [];
    const d = new Date();
    for (let i = 0; i < 4; i++) {
      const m = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - i, 1));
      const name = m.toLocaleString('en-US', { month: 'long', timeZone: 'UTC' });
      opts.push({ value: m.toISOString().slice(0, 7), label: i === 0 ? `This month (${name})` : `${name} ${m.getUTCFullYear()}` });
    }
    return opts;
  })();

  useEffect(() => {
    if (!month && monthOptions.length) setMonth(monthOptions[0].value);
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/publisher/publications`, { headers: authHeaders() });
        const data = await res.json();
        const list = data.publications || [];
        setPubs(list);
        if (list.length && !pubId) setPubId(list[0].id);
      } catch { setErr('Could not load publications'); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSummary = useCallback(async () => {
    if (!pubId || !month) return;
    setLoading(true); setErr(''); setDrillStatus(null); setDrill(null);
    try {
      const res = await fetch(`${API_URL}/api/publisher/publications/${pubId}/deliverability/summary?month=${month}&email_type=${emailType}`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
      setSummary(data);
    } catch (e) { setErr(typeof e.message === 'string' ? e.message : 'Failed to load'); setSummary(null); }
    finally { setLoading(false); }
  }, [pubId, month, emailType]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const openDrill = async (statusKey) => {
    setDrillStatus(statusKey); setDrill(null); setDrillLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/publisher/publications/${pubId}/deliverability/subscribers?month=${month}&email_type=${emailType}&status=${statusKey}`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
      setDrill(data);
    } catch (e) { setErr(typeof e.message === 'string' ? e.message : 'Failed to load list'); }
    finally { setDrillLoading(false); }
  };

  const counts = summary?.counts || {};

  return (
    <Wrap>
      <Title>Deliverability</Title>
      <SubTitle>How your subscriber emails are landing — delivered, bounced, marked spam, and opened. Data accrues from live sends (Resend).</SubTitle>

      <Row>
        <Select value={pubId} onChange={e => setPubId(e.target.value)}>
          {pubs.length === 0 && <option value="">No publications</option>}
          {pubs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
        <Select value={month} onChange={e => setMonth(e.target.value)}>
          {monthOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Select>
        <Select value={emailType} onChange={e => setEmailType(e.target.value)}>
          {EMAIL_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </Select>
      </Row>

      {err && <ErrMsg>{err}</ErrMsg>}

      {loading ? <Empty>Loading…</Empty> : !summary ? null : summary.total_sent === 0 ? (
        <Empty>No emails were sent for this publication in this period.</Empty>
      ) : (
        <>
          <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.55)', marginBottom: 12 }}>
            {summary.total_sent} email{summary.total_sent !== 1 ? 's' : ''} sent — click a tile to see who.
          </div>
          <TileGrid>
            {TILES.map(t => (
              <Tile key={t.key} $color={t.color} $active={drillStatus === t.key} onClick={() => openDrill(t.key)}>
                <TileNum $color={t.color}>{counts[t.key] ?? 0}</TileNum>
                <TileLabel>{t.label}{t.soft && <SoftTag>estimated</SoftTag>}</TileLabel>
              </Tile>
            ))}
          </TileGrid>
          <Note>ⓘ "Opened" is an estimate only — mail privacy features (e.g. Apple Mail) load the tracking pixel automatically, so opens are over-counted. Delivered / bounced / spam are reliable.</Note>

          {drillStatus && (
            <Section>
              <SectionTitle>
                {(TILES.find(t => t.key === drillStatus) || {}).label} — {emailType === 'all' ? 'all emails' : typeLabel(emailType)}, {month}
              </SectionTitle>
              {drillLoading ? <Empty>Loading…</Empty> : !drill ? null : drill.subscribers.length === 0 ? (
                <Empty>No subscribers in this bucket.</Empty>
              ) : (
                <>
                  <Table>
                    <thead><tr><Th>Email</Th><Th>Type</Th><Th>Subject</Th><Th>Sent</Th><Th>Last event</Th></tr></thead>
                    <tbody>
                      {drill.subscribers.map((s, i) => (
                        <tr key={i}>
                          <Td>{s.email}</Td>
                          <Td style={{ color: 'rgba(255,255,255,0.5)' }}>{typeLabel(s.email_type)}</Td>
                          <Td style={{ color: 'rgba(255,255,255,0.55)' }}>{s.subject || '—'}</Td>
                          <Td style={{ color: 'rgba(255,255,255,0.45)', whiteSpace: 'nowrap' }}>{fmtDate(s.sent_at)}</Td>
                          <Td style={{ color: 'rgba(255,255,255,0.45)', whiteSpace: 'nowrap' }}>
                            {fmtDate(s.last_event_at)}{s.bounce_kind ? ` · ${s.bounce_kind}` : ''}
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  {drill.capped && <Note>Showing the first 500 of {drill.total}. Narrow by email type to see more.</Note>}
                </>
              )}
            </Section>
          )}
        </>
      )}
    </Wrap>
  );
}
